<template>
  <div class="h-full w-full flex flex-col">
    <div class="h-full container mx-auto">
      <div class="px-8 flex flex-col h-full w-full py-8">
        <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
          <div class="flex items-center text-sm font-light">
            <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
            <span class="mx-2">/</span>
            <div v-if="showThis">
              <router-link to="/training-module" class="opacity-50 hover:opacity-100">Internship</router-link>
              <span class="mx-2">/</span>
            </div>
            <span>Internship Program</span>
          </div>
          <div class="font-bold">Internship Program</div>
        </div>
        <section class="mt-8 h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
          <div class="flex justify-between flex-wrap">
            <h1 class="text-base md:text-lg font-semibold">
              Internship Program List
            </h1>
            <div class="flex justify-start items-center">
              <div v-if="hasPermission('internship-create')" @click.stop="createGroup"
                class="flex items-center bg-ternary hover:bg-white border-ternary border px-4 py-2 rounded-lg cursor-pointer text-white hover:text-ternary">
                <icon-plus class="w-4 h-4"></icon-plus>
                <span class="text-xs ml-2">Create Group</span>
              </div>
              <div class="flex justify-start items-center flex-wrap" v-if="role == 'admin' || role == 'superadmin'">
                <div class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                  <div class="text-sm mr-2">Entity:</div>
                  <select v-model="filterByEntity" class="outline-none text-sm flex-grow">
                    <option value="">All</option>
                    <option :value="item.entity" v-for="(item, index) in filteredEntities" :key="index">{{
                      getEntityName(item.entity) }}</option>
                  </select>
                </div>
                <div class="w-full sm:w-auto mb-4 border rounded-lg px-4 h-8 flex items-center">
                  <div class="text-sm mr-2">Employer:</div>
                  <select v-model="filterByPartner" class="outline-none text-sm w-full sm:w-auto">
                    <option value="">All Employers</option>
                    <option :value="item.id" v-for="(item, index) in partnersList" :key="index">{{ item.en_company_name
                      }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-col mt-4" v-if="$apollo.queries.internships.loading">
            <table-shimmer :titleHeaders="[
              '#',
              'Ref No',
              'Group Name',
              'Training Entity',
              'Specialization',
              'Start Date',
              'End Date',
              'Status',
              'Candidates Count',
              'Actions',
            ]" :rows="first"></table-shimmer>
          </div>
          <div v-else-if="!$apollo.queries.internships.loading && !hasItems"
            class="h-full flex justify-center items-center">
            <h1 class="text-center text-xl font-medium">No internship found!</h1>
          </div>
          <div v-else class="flex flex-col mt-4">
            <div class="flex flex-col overflow-x-auto">
              <div class="inline-block min-w-full py-2">
                <div class="overflow-x-auto">
                  <table class="min-w-full text-left text-sm font-light">
                    <thead class="border-b font-medium dark:border-neutral-500">
                      <tr>
                        <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                          #
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Ref No
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Group Name
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Training Entity
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Specialization
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Start Date
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          End Date
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Status
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Candidates Count
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in items" :key="index" :class="getClass(item)"
                        class="border-b border-dashed dark:border-neutral-500">
                        <td class="whitespace-nowrap px-6 py-2 font-medium">
                          {{ paginatorInfo.firstItem + index }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <span class="font-medium cursor-pointer hover:underline hover:text-blue-600"
                            @click.stop="viewItem(item)">{{ item.ref_no }}</span>
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.group_name }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.partner.en_company_name }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.field_of_training }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ getInterviewDateTime(item.start_date) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ getInterviewDateTime(item.end_date) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ getStatus(item) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <div v-if="item.internship_candidates.length > 0" @click.stop="
                            showStudentsList(item.id, item.group_name)
                            " class="hover:underline hover:text-blue-600 cursor-pointer text-center"
                            title="Click to view the students list">
                            {{ item.internship_candidates.length }}
                          </div>
                          <div v-else class="text-center">
                            {{ item.internship_candidates.length }}
                          </div>
                        </td>
                        <td class="px-6 py-2">
                          <div class="flex">
                            <div v-if="hasPermission('internship-show')" @click.stop="viewItem(item)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                              <icon-eye
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                            </div>
                            <div v-if="
                              hasPermission('internship-edit') &&
                              isEditable(item)
                            " @click.stop="editItem(item)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                              <icon-pen
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-pen>
                            </div>
                            <div v-if="
                              hasPermission('internship-delete') &&
                              isEditable(item)
                            " @click.stop="deleteItem(item.id)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer">
                              <icon-trash
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-trash>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
            </div>
          </div>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="createModal" :title="`Create Training Group`" @close="closeModal">
              <create class="text-left" :get-all-partners="getAllPartners"
                :get-academic-year-terms="getAcademicYearTerms" @view-student-profile="viewStudentProfile"
                @review="review" @close="closeModal"></create>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="editModal" :title="`Edit Training Group`" @close="closeModal">
              <edit class="text-left" :internship-id="selectedInternshipId" :get-all-partners="getAllPartners"
                :get-academic-year-terms="getAcademicYearTerms" @view-student-profile="viewStudentProfile"
                @review="review" @close="closeModal" :selected-item="selectedItem"></edit>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="isViewItem" :title="`View Training Group - ${selectedItem.group_name}(${selectedItem.ref_no})`
              " height="" @close="closeModal">
              <internship-view class="text-left" :item="selectedItem" :college-student-programs="collegeStudentPrograms"
                @view-student-profile="viewStudentProfile"></internship-view>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-modal v-if="isShowCandidates" :title="`Training - ${selectedInternshipGroupName} - Students List`
              " height="" @close="closeModal">
              <internship-candidates :internship-id="selectedInternshipId" @close="closeModal"
                @view-student-profile="viewStudentProfile"></internship-candidates>
            </base-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="showFullModal" title="Student Profile" :inline-style="inlineStyle"
              @close="closeFullModal">
              <student-profile :student-id="studentId"></student-profile>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="isReviewItem" :title="getReviewInternshipTitle" height="" @close="closeReview">
              <internship-review :item="selectedItem" :internship-id="selectedInternshipId" :mode="mode"
                :form-data="formData" @view-student-profile="viewStudentProfile" @edit="editInternship"
                @created="onCreated" @updated="onUpdated" @close="closeReview"></internship-review>
            </base-full-modal>
          </transition>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import IconEye from "../../components/icons/IconEye.vue";
import IconPen from "../../components/icons/IconPen.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import TableShimmer from "../../components/shared/TableShimmer.vue";
import Create from "./create.vue";
import Edit from "./edit.vue";
import InternshipView from "./view.vue";
import { formattedDate } from "../../helpers/utils";
import StudentProfile from "../student-profile/index.vue";
import InternshipCandidates from "./internship-candidates.vue";
import InternshipReview from "./review.vue";
export default {
  name: "training-index",
  components: {
    IconEye,
    IconPen,
    IconTrash,
    IconPlus,
    Pagination,
    TableShimmer,
    Create,
    Edit,
    InternshipView,
    StudentProfile,
    InternshipCandidates,
    InternshipReview,
  },
  data() {
    return {
      internships: [],
      first: 10,
      page: 1,
      createModal: false,
      editModal: false,
      isViewItem: false,
      selectedItem: null,
      error: null,
      isAddStudents: false,
      isShowCandidates: false,
      selectedInternshipId: null,
      selectedInternshipGroupName: null,
      showFullModal: false,
      studentId: null,
      isReviewItem: false,
      formData: null,
      mode: "create",
      inlineStyle: "",
      activePartners: [],
      getAcademicYearTerms: [],
      collegeStudentProgramsList: [],
      entities: [],
      filterByEntity: "",
      filterByPartner: "",
      partnersList: [],
    };
  },
  created() {
    if (this.role == "admin" || this.role == "superadmin") {
      this.getPartnersList();
    }
  },
  watch: {
    internships(newVal) {
      if (newVal.error) {
        if (newVal.error_code == 403) {
          this.$router.replace("/no-permission");
        } else if (newVal.error_code == 401) {
          this.$router.replace("/");
        } else {
          alert(newVal.message);
          this.$router.replace("/");
        }
      }
    },
    filterByEntity() {
      this.filterByPartner = "";
      this.page = 1;
      this.getPartnersList();
    },
    filterByPartner() {
      this.page = 1;
    },
  },
  computed: {
    items() {
      return this.internships.data;
    },
    hasItems() {
      if (this.items && this.items.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    paginatorInfo() {
      return this.internships.paginatorInfo;
    },
    role() {
      return this.$store.getters.role;
    },
    getReviewInternshipTitle() {
      if (this.mode == "edit") {
        return `Review Training Group - ${this.formData.ref_no}`;
      } else {
        return `Review Training Group - ${this.formData.group_name}`;
      }
    },
    getAllPartners() {
      return this.activePartners.data;
    },
    collegeStudentPrograms() {
      return this.collegeStudentProgramsList.data;
    },
    filteredEntities() {
      return this.entities.data;
    },
    showThis() {
      if (this.role == "superadmin" || this.role == "admin") {
        return true;
      } else {
        return false;
      }
    },
  },
  apollo: {
    internships() {
      return {
        query: gql`
          query GetAllInternships(
            $first: Int!
            $page: Int!
            $entity: String
            $filter_by_partner: Int
          ) {
            internships(
              first: $first
              page: $page
              entity: $entity
              filter_by_partner: $filter_by_partner
            ) {
              error
              error_code
              message
              data {
                id
                ref_no
                token
                group_name
                field_of_training
                start_date
                end_date
                supervisor
                entity
                campus_code
                level_code
                program_code
                training_document_url
                workflow_with_role_id
                status
                is_completed
                status_text
                sector
                location
                partner {
                  id
                  en_company_name
                }
                internship_candidates {
                  student_id
                  student {
                    name_en
                  }
                }
                workflow_with_role {
                  status
                }
                internship_approvals {
                  user {
                    name
                  }
                  role
                  approved
                  approved_at
                  note
                }
                created_at
              }
              paginatorInfo {
                count
                currentPage
                firstItem
                hasMorePages
                lastItem
                lastPage
                perPage
                total
              }
            }
          }
        `,
        variables() {
          return {
            first: this.first,
            page: this.page,
            entity: this.filterByEntity,
            filter_by_partner: parseInt(this.filterByPartner),
          };
        },
        fetchPolicy: "cache-and-network",
      };
    },
    activePartners() {
      return {
        query: gql`
          query GetAllActivePartners {
            activePartners {
              error
              error_code
              message
              data {
                id
                entity_industry
                en_company_name
                city
                trade_license_number
                trade_license_authority
              }
            }
          }
        `,
      };
    },
    getAcademicYearTerms() {
      return {
        query: gql`
          query GetAcademicYearTerms {
            getAcademicYearTerms {
              term_start_date
              term_end_date
            }
          }
        `,
      };
    },
    collegeStudentProgramsList() {
      return {
        query: gql`
          query GetCollegeStudentProgramsList($is_enabled: Boolean) {
            collegeStudentProgramsList(is_enabled: $is_enabled) {
              data {
                code
                name
              }
            }
          }
        `,
        variables() {
          return {
            is_enabled: true,
          };
        },
      };
    },
    entities() {
      return {
        query: gql`
          query GetEntityList {
            entities {
              data {
                entity
              }
            }
          }
        `,
        fetchPolicy: "cache-and-network",
      };
    },
  },
  methods: {
    getEntityName(entity) {
      return entity.replace(/ATHS/g, "ATS");
    },
    getInterviewDateTime(date) {
      return formattedDate(date);
    },
    setPage(page) {
      this.page = page;
    },
    closeModal() {
      this.createModal = false;
      this.editModal = false;
      this.isViewItem = false;
      this.selectedItem = null;
      this.isAddStudents = false;
      this.isShowCandidates = false;
      this.selectedInternshipId = null;
      this.selectedInternshipGroupName = null;
    },
    showToast(message = "") {
      this.$apollo.queries.internships.refetch();
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "success",
        title: message,
      });
      this.closeModal();
    },
    onCreated(msg) {
      this.closeReview();
      this.showToast(`Internship Group Created Successfully. ${msg}`);
    },
    onUpdated(msg) {
      this.closeReview();
      this.showToast(`Internship Group updated successfully. ${msg}`);
    },
    createGroup() {
      this.mode = "create";
      this.createModal = true;
    },
    editItem(item) {
      this.mode = "edit";
      this.selectedInternshipId = item.id;
      this.selectedItem = item;
      this.editModal = true;
    },
    async deleteItem(id) {
      this.$swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            const DELETE_MUTATION = gql`
              mutation DeleteInternship($id: ID!) {
                deleteInternship(id: $id) {
                  error
                  message
                }
              }
            `;

            try {
              await this.$apollo
                .mutate({
                  mutation: DELETE_MUTATION,
                  variables: {
                    id: id,
                  },
                })
                .then((data) => {
                  if (data.data.deleteInternship.error) {
                    this.error = data.data.deleteInternship.message;
                  }
                })
                .catch((error) => {
                  this.error = error.message;
                });
            } catch (error) {
              this.error = error.message || "Please try again.";
            }

            if (this.error === null) {
              this.showToast("Training Group has been deleted.");
            } else {
              this.$swal
                .fire({
                  icon: "error",
                  title: "Oops...",
                  text: this.error,
                })
                .then((result) => {
                  if (result.value || result.dismiss) {
                    this.error = null;
                  }
                });
            }
          }
        });
    },
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      } else {
        const permissions = this.$store.getters.permissions;
        const permissionsArray = permissions.split(",");
        if (permissionsArray.includes(crudName)) {
          return true;
        } else {
          return false;
        }
      }
    },
    showStudentsList(id, groupName) {
      this.selectedInternshipId = id;
      this.selectedInternshipGroupName = groupName;
      this.isShowCandidates = true;
    },
    viewItem(item) {
      this.selectedItem = item;
      this.isViewItem = true;
    },
    addStudents(item) {
      this.selectedItem = item;
      this.selectedInternshipId = item.id;
      this.selectedInternshipGroupName = item.group_name;
      this.mode = "create";
      this.isAddStudents = true;
    },
    viewStudentProfile(studentId) {
      if (this.isReviewItem) {
        this.inlineStyle = "z-index: 51;";
      }
      this.studentId = studentId;
      this.showFullModal = true;
    },
    closeFullModal() {
      this.showFullModal = false;
      this.studentId = null;
    },
    review(formData) {
      this.formData = formData;
      this.isReviewItem = true;
    },
    closeReview() {
      this.isReviewItem = false;
    },
    editInternship() {
      this.isReviewItem = false;
    },
    getStatus(item) {
      if (item.workflow_with_role_id) {
        if (item.status == 2 || item.status == 0) {
          return item.status_text;
        } else {
          return item.workflow_with_role.status;
        }
      } else {
        return "Draft";
      }
    },
    isEditable(item) {
      if (item.workflow_with_role_id) {
        if (item.status == 2) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    },
    getClass(item) {
      if (item.is_completed) {
        return "bg-green-100";
      } else {
        if (item.status == 2) {
          return "bg-yellow-100";
        } else if (item.status == 0) {
          return "bg-red-100";
        } else {
          return "";
        }
      }
    },
    async getPartnersList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetActivePartnersList($entity: String) {
              activePartners(entity: $entity) {
                data {
                  id
                  en_company_name
                }
              }
            }
          `,
          variables: {
            entity: this.filterByEntity,
          },
          fetchPolicy: "network-only",
        });
        this.partnersList = data.activePartners.data;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
